// WhyOFinancialMarkets · three-card pillar section mirroring the
// live ofinancial.markets "Why OFinancial?" strip. Fully localised.
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Globe2, MapPin, Layers, ArrowRight } from "lucide-react";

const CARD_META = [
  { key: "c1", Icon: Globe2 },
  { key: "c2", Icon: MapPin },
  { key: "c3", Icon: Layers },
];

const DEFAULTS = {
  c1: {
    title: "Global Trading Platform",
    body: "OFinancial Markets is a licensed, multi-asset broker built for serious traders. We offer competitive pricing, fast execution and full transparency in every order ticket.",
  },
  c2: {
    title: "Local Market Expertise",
    body: "Local payment channels, native-language support and locally-tailored bonuses · we know the markets we serve, and we put real humans on the ground in every region.",
  },
  c3: {
    title: "Diversified Asset Portfolio",
    body: "1200+ instruments across forex, metals, indices, energies, crypto and shares CFDs · all from one MT5 login, with raw spreads from 0.0 pips on the Elite account.",
  },
};

export default function WhyOFinancialMarkets() {
  const { t } = useTranslation();
  return (
    <section data-testid="why-ofinancial-section" className="relative py-24 lg:py-28">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="text-center mb-12 lg:mb-16">
          <div className="pill-mono inline-flex mb-5">{t("why.pill", "Why traders pick us")}</div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-[-0.025em] leading-[1.05] text-text-1">
            {t("why.h1", "Why")} <span className="text-gradient-brand font-semibold">{t("why.hAccent", "OFinancial Markets")}</span>
          </h2>
          <p className="text-text-3 mt-5 max-w-2xl mx-auto text-[15px] leading-relaxed">
            {t("why.sub", "Three things matter most when you pick a broker · global reach, local depth and a real product range. We deliver all three.")}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 lg:gap-7">
          {CARD_META.map((c, i) => {
            const title = t(`why.${c.key}.title`, DEFAULTS[c.key].title);
            const body = t(`why.${c.key}.body`, DEFAULTS[c.key].body);
            return (
              <motion.div
                key={c.key}
                initial={{ opacity: 0, y: 22 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-10% 0px" }}
                transition={{ duration: 0.55, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                whileHover={{ y: -4 }}
                className="relative rounded-2xl border p-6 lg:p-8 overflow-hidden group"
                style={{ borderColor: "var(--line-strong)", background: "var(--surface)" }}
                data-testid={`why-card-${i + 1}`}
              >
                <div aria-hidden className="absolute inset-x-0 top-0 h-px opacity-0 group-hover:opacity-100 transition-opacity" style={{ background: "linear-gradient(90deg, transparent, color-mix(in srgb, var(--brand) 65%, transparent), transparent)" }} />
                <div
                  aria-hidden
                  className="absolute -top-20 -right-20 w-56 h-56 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                  style={{ background: "radial-gradient(circle, color-mix(in srgb, var(--brand) 14%, transparent), transparent 65%)" }}
                />

                <div className="font-mono text-[12px] uppercase tracking-[0.22em] text-text-3 mb-4">{String(i + 1).padStart(2, "0")} · {title.split(" ")[0]}</div>
                <c.Icon size={28} strokeWidth={1.6} className="text-brand mb-5" />
                <h3 className="font-display text-xl font-medium text-text-1 mb-3 leading-tight">{title}</h3>
                <p className="text-text-3 text-[13.5px] leading-relaxed font-light">{body}</p>
              </motion.div>
            );
          })}
        </div>

        <div className="text-center mt-12">
          <Link to="/about" className="btn-ghost-rotating text-sm px-7" data-testid="why-cta">
            {t("why.cta", "More about us")} <ArrowRight size={14} />
          </Link>
        </div>
      </div>
    </section>
  );
}
